/**
 * 腾讯云 IM 连接层：全局生命周期管理
 *
 * 职责
 * - 维护全局单例 IM 客户端（`globalChatInstance`）
 * - 登录 / 登出 / 销毁 / 过期自恢复 的完整生命周期
 * - 暴露 `subscribeIMInstance` 供业务层感知实例变更（过期自恢复后会换实例）
 *
 * 非职责
 * - 不处理任何业务逻辑（群组、消息、Profile 等都不在这里）
 * - 不直接跟 UI 交互，仅通过可选 `messageApi` 给出用户级提示
 */
import TencentCloudChat from '@tencentcloud/lite-chat';
import { IM_SDK_APP_ID } from '@/config/im.config';
import { getErrorMessage } from '@/utils/errors';
import { createLogger } from '@/utils/logger';
import { getUserSig } from './api';
import type {
  IMClientHandle,
  IMInstanceListener,
  InitIMOptions,
} from './types';

const logger = createLogger('IMClient');

// ==================== 单例状态 ====================

let globalChatInstance: any = null;
let globalIMUserId = '';
let globalInitPromise: Promise<IMClientHandle> | null = null;

/** 过期自恢复锁：避免 ERROR 与 KICKED_OUT 同时触发导致并发恢复 */
let globalRecoverPromise: Promise<void> | null = null;

/**
 * 会话代数计数器，用于解决"初始化/恢复 vs 销毁"并发：
 * - 每次 `destroyIMClient` 运行时自增
 * - `initIMClient` 进入 IIFE 时捕获当前代数；登录成功准备挂单例前再核对，
 *   如果代数已变（说明过程中被 destroy / 登出），就丢弃新实例而不是挂回去
 * 这样能避免"登出后，恢复流程完成又把自己注册回来"的幽灵连接
 */
let sessionGeneration = 0;

/** 实例变更订阅集合 */
const imInstanceListeners = new Set<IMInstanceListener>();

// ==================== 订阅 API ====================

/**
 * 订阅 IM 实例变更。UserSig 过期自动恢复后会创建新的 IM 实例，
 * 所有业务侧的监听器（SDK_READY / MESSAGE_RECEIVED 等）需要在
 * 回调里重新挂到新实例上，否则会出现"重连后静默丢消息"。
 *
 * 返回 unsubscribe 函数，组件卸载时记得调用。
 */
export function subscribeIMInstance(listener: IMInstanceListener): () => void {
  imInstanceListeners.add(listener);
  return () => {
    imInstanceListeners.delete(listener);
  };
}

function notifyIMInstanceChange() {
  if (!globalChatInstance || !globalIMUserId) return;
  let failedCount = 0;
  imInstanceListeners.forEach((listener) => {
    try {
      listener(globalChatInstance, globalIMUserId);
    } catch {
      failedCount += 1;
    }
  });
  if (failedCount > 0) {
    logger.error('IM 实例订阅回调执行异常', {
      failedCount,
      totalListeners: imInstanceListeners.size,
    });
  }
}

// ==================== 过期识别 ====================

/**
 * 是否 UserSig 过期错误。兼容 login reject、SDK ERROR 事件 data
 * （`{code, message}`）以及部分兜底消息文案。
 */
export function isUserSigExpiredError(error: unknown): boolean {
  const shape =
    typeof error === 'object' && error !== null
      ? (error as {
          code?: number | string;
          message?: string;
          data?: { message?: string };
        })
      : {};
  const code = shape.code;
  const message = (shape.message || shape.data?.message || '').toLowerCase();
  return (
    code === 70001 ||
    message.includes('usersig 已过期') ||
    message.includes('70001') ||
    message.includes('usersig expired')
  );
}

// ==================== 初始化 / 读取 / 等待就绪 ====================

/**
 * 初始化全局 IM SDK 实例（单例）
 *
 * - 已经初始化则直接复用
 * - 登录成功或应用启动（F5）时调用一次，后续页面直接通过 `getIMClient`
 *   或 `waitForIMReady` 取用
 * - 登录期 UserSig 过期会销毁当前实例并递归调自己 `forceRefresh=true` 重建，
 *   以避免同一个实例上残留脏的连接态
 */
export async function initIMClient(
  options?: InitIMOptions,
): Promise<IMClientHandle> {
  if (globalChatInstance && globalIMUserId) {
    return { chat: globalChatInstance, imUserId: globalIMUserId };
  }

  if (globalInitPromise) {
    return globalInitPromise;
  }

  const {
    forceRefresh = false,
    messageApi,
    silent = false,
    __useExpireUserSigForTest = false,
  } = options || {};

  // 本次 init 的会话代数快照：若 publish 前代数变了，说明已被并发 destroy 取消
  const myGeneration = sessionGeneration;

  globalInitPromise = (async () => {
    try {
      const userSigResponse = await getUserSig({
        force_refresh: forceRefresh,
        ...(__useExpireUserSigForTest ? { 'expire-usersig': true } : {}),
      });
      if (userSigResponse.status !== 'success' || !userSigResponse.data) {
        throw new Error('获取 UserSig 失败');
      }

      const { user_id, usersig, expire_usersig } = userSigResponse.data;

      if (!IM_SDK_APP_ID || IM_SDK_APP_ID === 0) {
        throw new Error(
          'SDKAppID 配置无效，请在 .env 文件中配置 IM_SDK_APP_ID',
        );
      }

      // 选择用于登录的签名：
      // - 正常路径用 `usersig`（长期签名，通常 3600s）
      // - 测试路径用 `expire_usersig`（1 秒预览签），以便触发 SDK ERROR 事件
      const signatureToUse =
        __useExpireUserSigForTest && expire_usersig ? expire_usersig : usersig;

      if (__useExpireUserSigForTest) {
        logger.warn(
          '[TEST] 使用 1 秒有效期的预览签登录 IM，用于验证 UserSig 过期兜底流程',
        );
      }

      const chatInstance = TencentCloudChat.create({
        SDKAppID: IM_SDK_APP_ID,
      });
      chatInstance.setLogLevel(0);

      try {
        await chatInstance.login({
          userID: user_id,
          userSig: signatureToUse,
        });
      } catch (loginError) {
        // 登录期检测到 UserSig 过期：不在同一个实例上重试（会留脏的连接态
        // 导致延迟的 KICKED_OUT 再次触发一次恢复 → "两次 force_refresh"）。
        // 改为：销毁当前实例 → 清掉 globalInitPromise → 递归调自己 forceRefresh=true。
        // 递归时不传 __useExpireUserSigForTest，避免"1 秒过期 → 重连 → 1 秒过期"死循环。
        if (isUserSigExpiredError(loginError) && !forceRefresh) {
          logger.info('登录时 UserSig 已过期，销毁实例并 force_refresh 重建...');
          try {
            chatInstance.destroy();
          } catch (destroyErr) {
            logger.warn('销毁失败的 chatInstance 时出错（忽略）', destroyErr);
          }
          globalInitPromise = null;
          return initIMClient({
            forceRefresh: true,
            messageApi,
            silent,
          });
        }
        throw loginError;
      }

      // 登录成功到这里，才正式挂单例。若期间发生过 destroy（登出 / 被踢等），
      // 代数会 +1，这里发现不一致就直接销毁新实例，保持"已登出 = 无 IM 实例"的不变式。
      if (sessionGeneration !== myGeneration) {
        logger.warn(
          'IM 初始化完成时发现已被并发 destroy（登出或自恢复取消），丢弃新实例',
        );
        try {
          chatInstance.destroy();
        } catch {
          // 忽略
        }
        globalInitPromise = null;
        throw new Error('IM 初始化已被取消（并发 destroy）');
      }

      globalChatInstance = chatInstance;
      globalIMUserId = user_id;
      attachExpirationRecoveryListeners(chatInstance);

      logger.info('IM 初始化成功', { userId: user_id });
      return { chat: chatInstance, imUserId: user_id };
    } catch (error) {
      logger.error('IM 初始化失败', error);

      if (!silent) {
        const isExpired = isUserSigExpiredError(error);
        messageApi?.error?.(
          isExpired
            ? 'IM 登录失败：UserSig 已过期，请稍后重试'
            : `IM 初始化失败: ${getErrorMessage(error, '未知错误')}`,
        );
      }

      globalInitPromise = null;
      throw error;
    }
  })();

  return globalInitPromise;
}

/**
 * 获取已存在的 IM 实例（如果尚未初始化则返回 null）。
 * 业务层同步读取单例时使用。
 */
export function getIMClient(): IMClientHandle | null {
  if (globalChatInstance && globalIMUserId) {
    return { chat: globalChatInstance, imUserId: globalIMUserId };
  }
  return null;
}

/**
 * 业务层用：等待全局 IM 就绪并返回实例。
 *
 * - 已就绪直接返回；
 * - 正在初始化/恢复中：等待当前 `globalInitPromise` 完成；
 * - 完全未初始化（极少数竞态，比如组件先于 app.tsx 的拉起执行）：返回 null，
 *   调用方自行决定是否降级或轮询。
 *
 * 业务层**不**负责触发 `initIMClient`——登录成功 / F5 启动时由生命周期层统一拉起。
 * 这里只消费已有实例。过期自恢复后的新实例通过 `subscribeIMInstance` 通知。
 */
export async function waitForIMReady(): Promise<IMClientHandle | null> {
  const existing = getIMClient();
  if (existing) return existing;

  if (globalInitPromise) {
    try {
      return await globalInitPromise;
    } catch {
      return null;
    }
  }

  return null;
}

// ==================== 销毁 ====================

/**
 * 销毁全局 IM 实例，登出并清理资源。
 *
 * 容错策略：
 * - logout 出 `code: 2024`（"用户未登录"）属于 SDK 已不在登录态的正常情况
 *   （例如刚被踢下线 / 刚 login 失败 / 网络断连后 SDK 本地状态回退），走 info 日志
 * - 其他 logout 错误记 error，但**仍然继续**执行 destroy 与单例清空，避免实例泄漏
 */
export async function destroyIMClient(): Promise<void> {
  // 第一步：先 bump 代数。即使当前没有实例（init 还在进行中），也要让
  // 正在登录的 initIMClient 在 publish 阶段检查到"已被销毁/登出"而放弃挂载。
  sessionGeneration++;

  if (!globalChatInstance) {
    // 可能正在 init：丢弃占位 promise，让新的调用者不会 await 到一个随后会
    // 被"代数不一致"拒绝的幽灵实例（awaiter 会 catch 到异常，自行处理降级）。
    globalInitPromise = null;
    return;
  }

  try {
    await globalChatInstance.logout();
  } catch (err: unknown) {
    const code = (err as { code?: number } | null | undefined)?.code;
    if (code === 2024) {
      logger.info('IM 已处于未登录状态，跳过 logout');
    } else {
      logger.error('IM logout 失败（仍会继续销毁实例）', err);
    }
  }

  try {
    globalChatInstance.destroy();
  } catch (err) {
    logger.error('IM destroy 失败', err);
  }

  globalChatInstance = null;
  globalIMUserId = '';
  globalInitPromise = null;
  logger.info('IM 实例销毁完成');
}

// ==================== 互踢重连（业务层调用） ====================

/**
 * multipleAccount 互踢后由用户手动触发的重连：
 * 销毁旧实例 → 用现有 UserSig 重新初始化 → 通知业务层重绑监听器。
 *
 * 与 `recoverFromUserSigExpired` 的区别：
 * - 不需要 forceRefresh（签名未过期）
 * - 不需要并发锁（由用户点击触发，不会并发）
 */
export async function reconnectIMClient(): Promise<IMClientHandle> {
  logger.info('[IM] 用户触发 multipleAccount 重连...');
  await destroyIMClient();
  const handle = await initIMClient({ forceRefresh: false, silent: false });
  notifyIMInstanceChange();
  logger.info('[IM] multipleAccount 重连成功');
  return handle;
}

// ==================== 过期自恢复（内部） ====================

/**
 * 连接层 UserSig 过期自恢复：销毁旧实例 → 强制刷新签名 → 重新初始化
 * → 通过 `subscribeIMInstance` 通知业务层重绑监听器。
 *
 * - 被 `ERROR(70001)` 与 `KICKED_OUT(userSigExpired)` 两个事件共同调用
 * - 使用 `globalRecoverPromise` 保证并发只执行一次
 * - 内部调用 `initIMClient` 时刻意不带 `__useExpireUserSigForTest`，
 *   避免测试场景下陷入"1 秒过期 → 重连 → 1 秒过期"的死循环
 */
async function recoverFromUserSigExpired(reason: string): Promise<void> {
  if (globalRecoverPromise) return globalRecoverPromise;

  globalRecoverPromise = (async () => {
    logger.warn(`[IM] 检测到 UserSig 过期（${reason}），开始自动恢复...`);
    try {
      await destroyIMClient();
      await initIMClient({ forceRefresh: true, silent: true });
      logger.info(`[IM] UserSig 过期后自动恢复成功（${reason}）`);
      notifyIMInstanceChange();
    } catch (err) {
      logger.error(`[IM] UserSig 过期后自动恢复失败（${reason}）`, err);
    } finally {
      globalRecoverPromise = null;
    }
  })();

  return globalRecoverPromise;
}

/**
 * 在新创建的 IM 实例上挂载"仅处理过期恢复"的事件监听器。
 * 仅在 `initIMClient` 创建实例成功后调用一次，业务层无需再关心过期恢复。
 */
function attachExpirationRecoveryListeners(chatInstance: any): void {
  const onSDKError = (event: any) => {
    if (isUserSigExpiredError(event?.data)) {
      void recoverFromUserSigExpired('ERROR 70001');
    }
  };
  const onSDKKickedOut = (event: any) => {
    if (event?.data?.type === 'userSigExpired') {
      void recoverFromUserSigExpired('KICKED_OUT userSigExpired');
    }
  };
  chatInstance.on(TencentCloudChat.EVENT.ERROR, onSDKError);
  chatInstance.on(TencentCloudChat.EVENT.KICKED_OUT, onSDKKickedOut);
}
